import type {
  CSSProperties,
  HTMLAttributes,
  TableHTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from "react";

function mergeClasses(...classes: Array<string | undefined | false>): string {
  return classes.filter(Boolean).join(" ");
}

type DivProps = HTMLAttributes<HTMLDivElement>;
type TableProps = TableHTMLAttributes<HTMLTableElement>;
type SectionProps = HTMLAttributes<HTMLTableSectionElement>;
type RowProps = HTMLAttributes<HTMLTableRowElement>;
type HeadCellProps = ThHTMLAttributes<HTMLTableCellElement>;
type CellProps = TdHTMLAttributes<HTMLTableCellElement>;

const whiteBackground: CSSProperties = {
  backgroundColor: "#ffffff",
  backgroundImage: "none",
};

/**
 * Shared LDS table shell.
 *
 * The component deliberately neutralises the legacy global indigo table styles.
 * Keep table colours here instead of styling individual report pages.
 */
export function LdsTableCard({ className, ...props }: DivProps) {
  return (
    <div
      className={mergeClasses(
        "relative rounded-[24px] border border-slate-200/80 bg-white shadow-sm",
        "dark:border-white/10 dark:bg-slate-900/80",
        className
      )}
      {...props}
    />
  );
}

/** Keeps wide tables scrollable without adding another border or radius. */
export function LdsTableScroll({ className, ...props }: DivProps) {
  return (
    <div
      className={mergeClasses("overflow-x-auto", className)}
      {...props}
    />
  );
}

export function LdsTable({ className, ...props }: TableProps) {
  return (
    <table
      className={mergeClasses(
        "w-full min-w-full border-separate border-spacing-0 text-left",
        className
      )}
      {...props}
    />
  );
}

/** White LDS header, protected against legacy global indigo/gradient selectors. */
export function LdsTableHead({ className, style, ...props }: SectionProps) {
  return (
    <thead
      className={mergeClasses(
        "!bg-white !bg-none !text-slate-600 dark:!bg-slate-900 dark:!text-slate-300",
        "[&>tr]:!bg-white [&>tr]:!bg-none dark:[&>tr]:!bg-slate-900",
        className
      )}
      style={{ ...whiteBackground, ...style }}
      {...props}
    />
  );
}

export function LdsTableBody({ className, ...props }: SectionProps) {
  return (
    <tbody
      className={mergeClasses(
        "bg-white dark:bg-slate-900",
        "[&>tr+tr>td]:border-t [&>tr+tr>td]:border-slate-200/80 dark:[&>tr+tr>td]:border-white/10",
        className
      )}
      {...props}
    />
  );
}

export function LdsTableRow({ className, ...props }: RowProps) {
  return (
    <tr
      className={mergeClasses(
        "bg-white transition-colors hover:bg-slate-50/80 dark:bg-slate-900 dark:hover:bg-white/[0.035]",
        className
      )}
      {...props}
    />
  );
}

export function LdsTableHeadCell({ className, style, ...props }: HeadCellProps) {
  return (
    <th
      className={mergeClasses(
        "!border-b !border-slate-200/80 !bg-white !bg-none px-4 py-4",
        "whitespace-nowrap text-xs font-bold uppercase tracking-[0.08em] !text-slate-600",
        "first:rounded-tl-[23px] last:rounded-tr-[23px]",
        "dark:!border-white/10 dark:!bg-slate-900 dark:!text-slate-300",
        className
      )}
      style={{ ...whiteBackground, ...style }}
      {...props}
    />
  );
}

export function LdsTableCell({ className, ...props }: CellProps) {
  return (
    <td
      className={mergeClasses(
        "px-4 py-4 align-middle text-sm text-slate-700 dark:text-slate-200",
        className
      )}
      {...props}
    />
  );
}
